import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormBuilder, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { VendorService } from '../../services/vendor.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { GlobalsService } from '../../services/globals.service';

@Component({
	selector: 'app-vendor-create',
	templateUrl: './vendor-create.component.html',
	styleUrls: ['./vendor-create.component.scss']
})
export class VendorCreateComponent implements OnInit {
	@Input() embedded = false;
	@Input() initialName = '';
	@Output() vendorCreated = new EventEmitter<string>();
	@Output() cancelled = new EventEmitter<void>();

	vd: any = {};
	banking = false;
	taxes: any[] = [];
	currencies: any[] = [];
	bankaccounts: any[] = [];
	types: any = {};
	zones: any = {};
	paymentterms: any[] = [];
	vendorForm: any = false;
	active_vendors: any[] = [];
	supplieridManuallyEdited = false;
	color = '';
	defaults: any = {};

	constructor(
		private vendorService: VendorService,
		private location: Location,
		public router: Router,
		private globalSearchService: GlobalSearchService,
		private fb: UntypedFormBuilder,
		public globalsService: GlobalsService
	) {
		this.color = this.globalSearchService.getColor();
	}

	ngOnInit(): void {
		this.defaults = { ...this.defaults, ...(this.globalsService.config?.defaults || {}) };
		this.banking = !!this.globalsService.config?.vendorRequireBankingInfo;
		this.buildForm();
		this.bindVendorIdGeneration();
		if (this.initialName) { this.vendorForm.get('suppname')?.setValue(this.initialName); }
		this.applyVendorContactRequiredFromConfig();
		this.loadLookups();

		this.vendorService.getVendors().subscribe((vendors: any) => {
			this.active_vendors = vendors || [];
			this.vendorForm.get('supplierid')?.updateValueAndValidity({ emitEvent: false });
		});

		this.globalSearchService.configsubscription.subscribe(config => {
			if (config?.defaults) {
				this.defaults = { ...this.defaults, ...config.defaults };
				this.applyDefaults();
			}
			this.applyVendorContactRequiredFromConfig();
		});
	}

	private buildForm(): void {
		this.vendorForm = this.fb.group({
			active: [1],
			supplierid: ['', [Validators.required, this.vendIdExists()]],
			suppname: ['', [Validators.required]],
			email: [''],
			address1: ['', [Validators.required]],
			address2: [''],
			address3: ['', [Validators.required]],
			address4: [this.defaults.region || '', [Validators.required]],
			address5: ['', [Validators.required]],
			address6: [this.defaults.country || 'United States', [Validators.required]],
			supptype: [this.defaults.suppliertype || '', [Validators.required]],
			currcode: [this.defaults.currency || '', [Validators.required]],
			paymentterms: [this.defaults.terms || '', [Validators.required]],
			bankact: [this.defaults.bank || '', [Validators.required]],
			remittance: [this.defaults.remit || '', [Validators.required]],
			taxgroupid: [this.defaults.taxauth || '', [Validators.required]],
			factorcompanyid: [''],
			telephone: [''],
			url: ['']
		});

		if (this.banking) {
			this.addBankingControls();
		}
	}

	private bindVendorIdGeneration(): void {
		this.vendorForm.get('suppname').valueChanges.subscribe((name: string) => {
			if (name && !this.supplieridManuallyEdited) {
				this.vendorForm.get('supplierid').setValue(this.generateVendorId(name), { emitEvent: false });
			}
		});

		this.vendorForm.get('supplierid').valueChanges.subscribe(() => {
			if (this.vendorForm.get('supplierid').dirty) {
				this.supplieridManuallyEdited = true;
			}
		});
	}

	private loadLookups(): void {
		this.globalsService.getTypes().subscribe(details => { this.types = details; });
		this.globalsService.getBankAccounts().subscribe(details => { this.bankaccounts = details; });
		this.globalsService.getTerms().subscribe(details => { this.paymentterms = details; });
		this.globalsService.getTaxes().subscribe(details => { this.taxes = details; });
		this.globalsService.getCurrencies().subscribe(details => { this.currencies = details; });
		this.globalsService.getDefaultZones().subscribe(details => { this.zones = details; });
	}

	private addBankingControls(): void {
		const controls: { [key: string]: FormControl } = {
			sin: new FormControl('', [Validators.required]),
			bn: new FormControl('', [Validators.required]),
			bankbeneficiary: new FormControl('', [Validators.required]),
			bankname: new FormControl('', [Validators.required]),
			bankaddress1: new FormControl('', [Validators.required]),
			bankaddress2: new FormControl(''),
			bankaddress3: new FormControl('', [Validators.required]),
			bankaddress4: new FormControl('', [Validators.required]),
			bankaddress5: new FormControl('', [Validators.required]),
			bankaddress6: new FormControl('', [Validators.required]),
			bankaccountno: new FormControl('', [Validators.required]),
			iban: new FormControl('', [Validators.required]),
			banktransitno: new FormControl('', [Validators.required]),
			bankcurrencypref: new FormControl('', [Validators.required]),
			bankswiftcode: new FormControl('', [Validators.required]),
		};

		Object.keys(controls).forEach(key => this.vendorForm.addControl(key, controls[key]));
		this.bindEitherRequired('bn', 'sin');
		this.bindEitherRequired('bankaccountno', 'iban');
		this.vendorForm.get('bankaddress6').valueChanges.subscribe((country: string) => this.setBankCurrencyFromCountry(country || ''));
		this.vendorForm.updateValueAndValidity();
	}

	private bindEitherRequired(firstName: string, secondName: string): void {
		const first = this.vendorForm.get(firstName);
		const second = this.vendorForm.get(secondName);
		first.valueChanges.subscribe((value: any) => this.setRequiredWhenPeerEmpty(second, value));
		second.valueChanges.subscribe((value: any) => this.setRequiredWhenPeerEmpty(first, value));
	}

	private setRequiredWhenPeerEmpty(control: AbstractControl, peerValue: any): void {
		if (peerValue?.length) {
			control.clearValidators();
		} else {
			control.setValidators([Validators.required]);
		}
		control.updateValueAndValidity({ emitEvent: false });
	}

	private setBankCurrencyFromCountry(country: string): void {
		const normalized = country.toLowerCase();
		if (normalized.includes('canada')) {
			this.vendorForm.get('bankcurrencypref').setValue('CAD');
		} else if (normalized.includes('united states')) {
			this.vendorForm.get('bankcurrencypref').setValue('USD');
		}
		this.vendorForm.get('bankcurrencypref').updateValueAndValidity();
	}

	private applyVendorContactRequiredFromConfig(): void {
		if (!this.vendorForm || typeof this.vendorForm.get !== 'function') {
			return;
		}
		const required = !!this.globalsService.config?.vendorRequireEmailPhone;
		['email', 'telephone'].forEach(controlName => {
			const control = this.vendorForm.get(controlName);
			control.setValidators(required ? [Validators.required] : []);
			control.updateValueAndValidity();
		});
	}

	private applyDefaults(): void {
		const defaultMap: { [key: string]: any } = {
			address4: this.defaults.region,
			address6: this.defaults.country || 'United States',
			supptype: this.defaults.suppliertype,
			currcode: this.defaults.currency,
			paymentterms: this.defaults.terms,
			bankact: this.defaults.bank,
			remittance: this.defaults.remit,
			taxgroupid: this.defaults.taxauth,
		};
		Object.keys(defaultMap).forEach(controlName => {
			if (defaultMap[controlName] != null) {
				this.vendorForm.get(controlName)?.setValue(defaultMap[controlName], { emitEvent: false });
			}
		});
	}

	updateVendor(): void {
		if (this.vendorForm.valid) {
			this.vendorService.saveVendor(this.vendorForm.value).subscribe({
				next: () => {
					const supplierid = String(this.vendorForm.controls.supplierid.value);
					if (this.embedded) { this.vendorCreated.emit(supplierid); }
					else { this.router.navigate(['vendors/' + supplierid]); }
				},
				error: (error: any) => {
					const msg = error?.error?.message || error?.message || 'Error saving vendor';
					const isDuplicate = /duplicate|already exists|supplierid|unique constraint/i.test(msg);
					if (isDuplicate) {
						this.vendorForm.get('supplierid').setErrors({ duplicate: true });
					}
					this.globalSearchService.showNotification(
						isDuplicate ? 'Vendor ID already exists. Please choose a different ID.' : msg,
						'danger', 'bottom', 'right'
					);
				}
			});
		} else {
			this.getFormValidationErrors();
		}
	}

	back(): void {
		if (this.embedded) {
			this.cancelled.emit();
			return;
		}
		this.location.back();
	}

	getAddress(place: any, bank: any = false): void {
		const components = place['address_components'];
		const prefix = bank ? 'bank' : '';

		this.vendorForm.get(`${prefix}address1`).setValue(this.parseComponentShortName(components, ['street_number', 'route']));
		this.vendorForm.get(`${prefix}address2`).setValue(this.parseComponentShortName(components, ['subpremise']));
		this.vendorForm.get(`${prefix}address3`).setValue(this.parseComponentShortName(components, ['locality']));
		this.vendorForm.get(`${prefix}address4`).setValue(this.parseComponentShortName(components, ['administrative_area_level_1']));
		this.vendorForm.get(`${prefix}address5`).setValue(this.parseComponentShortName(components, ['postal_code']));
		this.vendorForm.get(`${prefix}address6`).setValue(this.parseComponentLongName(components, ['country']));

		if (bank) {
			return;
		}

		if (place['name']) {
			this.vendorForm.get('suppname').setValue(place['name']);
			if (!this.supplieridManuallyEdited) {
				this.vendorForm.get('supplierid').setValue(this.generateVendorId(place['name']));
			}
		}
		if (place['formatted_phone_number']) {
			this.vendorForm.get('telephone').setValue(place['formatted_phone_number']);
		}
		if (place['website']) {
			this.vendorForm.get('url').setValue(place['website']);
		}
	}

	getFormValidationErrors(): void {
		this.vendorForm.markAllAsTouched();
		Object.keys(this.vendorForm.controls).forEach(key => {
			const errors: ValidationErrors = this.vendorForm.get(key).errors;
			if (errors) {
				Object.keys(errors).forEach(errorKey => {
					this.globalSearchService.showNotification(`${key} ${errorKey}`, 'danger', 'bottom', 'right');
				});
			}
		});
	}

	private vendIdExists(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value?.toUpperCase();
			if (!value) {
				return null;
			}
			return this.active_vendors.some(v => v.supplierid.toUpperCase() === value) ? { isNewvendor: true } : null;
		};
	}

	private parseComponentShortName(components: any[], types: string[]): string {
		const result: string[] = [];
		components.forEach(comp => {
			comp.types.forEach((local: string) => {
				if (types.includes(local) && comp.short_name) {
					result.push(comp.short_name);
				}
			});
		});
		return result.join(' ');
	}

	private parseComponentLongName(components: any[], types: string[]): string {
		const result: string[] = [];
		components.forEach(comp => {
			comp.types.forEach((local: string) => {
				if (types.includes(local) && comp.long_name) {
					result.push(comp.long_name);
				}
			});
		});
		return result.join(' ');
	}

	private generateVendorId(name: string): string {
		if (!name) {
			return '';
		}

		const cleanName = name
			.replace(/\s+(Inc|LLC|Ltd|Corp|Corporation|Company|Co)(\.)?$/i, '')
			.trim();

		let id = cleanName.split(' ').filter(w => w.length).map(w => w[0].toUpperCase()).join('');

		if (id.length < 3) {
			id = cleanName.split(' ').map(w => w.substring(0, 2).toUpperCase()).join('').substring(0, 6);
		}

		id = id.substring(0, 10);

		if (id.length < 3) {
			id = cleanName.replace(/\s/g, '').substring(0, 10).toUpperCase();
		}

		const existingIds = this.active_vendors?.map((v: any) => v.supplierid.toUpperCase()) || [];
		let uniqueId = id;
		let counter = 1;

		while (existingIds.includes(uniqueId.toUpperCase()) && counter < 1000) {
			const suffix = counter.toString();
			uniqueId = id.substring(0, 10 - suffix.length) + suffix;
			counter++;
		}

		return uniqueId;
	}
}
